import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { authenticate } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, {
        username,
        password,
      });
      authenticate(data.token, data.user);
      toast.success(mode === "login" ? "Welcome back!" : "Account created — let's glow!");
      navigate("/create");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-20">
        <motion.div
          className="bg-white rounded-3xl p-8 sm:p-10 soft-shadow border border-black/5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Sparkles className="w-8 h-8 mb-5" style={{ color: "var(--primary)" }} />
          <h1 className="font-heading text-3xl text-charcoal">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-muted-c mt-2 mb-8">
            {mode === "login" ? "Log in to see your future self." : "Just a username & password — no email needed."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Username</label>
              <input
                data-testid="auth-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="mt-2 w-full rounded-2xl border border-black/10 px-4 py-3 bg-[#FAF8F5] outline-none focus:border-[var(--primary)] transition-colors"
                placeholder="yourname"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Password</label>
              <input
                data-testid="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                className="mt-2 w-full rounded-2xl border border-black/10 px-4 py-3 bg-[#FAF8F5] outline-none focus:border-[var(--primary)] transition-colors"
                placeholder="••••••"
              />
            </div>
            <button
              data-testid="auth-submit"
              disabled={loading}
              className="btn-primary w-full py-3.5 font-semibold mt-2"
            >
              {loading ? "Please wait…" : mode === "login" ? "Log in" : "Sign up"}
            </button>
          </form>

          <button
            data-testid="auth-toggle"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-6 text-sm text-muted-c hover:text-charcoal transition-colors w-full text-center"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
